import type { ReactNode } from "react";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import styles from "./judge-dark-head.module.css";

/**
 * The dark ground on its own (`EX-PUB22`): the near-black, the dot grid and
 * the glow, around a page-width container. {@link JudgeDarkHead} lays its
 * slots out on it; the contest page lays out its own, because its door has to
 * come before the description on a phone.
 */
export function JudgeDarkFrame({ labelledBy, children }: { labelledBy: string; children: ReactNode }) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="relative isolate overflow-hidden bg-neutral-950 text-white"
    >
      <div className={styles.grid} aria-hidden />
      <div className={styles.glow} aria-hidden />
      <Container>{children}</Container>
    </section>
  );
}

/**
 * The head of a Judge inner page, `EX-PUB21` (the "Judge pages" proposal of
 * 2026-09-23): the Judge bar's near-black carried down into the page, with the
 * Arena's dot grid and one glow, and the page's light body under it. The
 * problem library is its first reader; the contest, leaderboard and
 * submissions pages take it in `EX-PUB22` and `EX-PUB23`, which is why it takes
 * its content as slots rather than knowing about problems.
 *
 * `children` sits under the description (the library's difficulty meter) and
 * `aside` is the right-hand panel from `lg` up, stacked below on a phone.
 * `tabs` (`EX-CPG03`, Wave 30 decision 5) is the page's sections on the
 * head's bottom edge, a `HeadTabs`, the head's bottom padding taken in to
 * meet it.
 */
export function JudgeDarkHead({
  eyebrow,
  eyebrowIcon,
  title,
  titleId,
  description,
  children,
  aside,
  tabs,
}: {
  eyebrow: string;
  eyebrowIcon?: ReactNode;
  title: string;
  titleId: string;
  description?: string;
  children?: ReactNode;
  aside?: ReactNode;
  tabs?: ReactNode;
}) {
  return (
    <JudgeDarkFrame labelledBy={titleId}>
      <div
        className={cn(
          "grid gap-8 pt-10 sm:pt-12 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-end lg:gap-14",
          tabs ? "pb-7 sm:pb-8" : "pb-9 sm:pb-11",
        )}
      >
        <div className="min-w-0">
          <p className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.16em] text-accent-peach">
            {eyebrowIcon ? (
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary"
                aria-hidden
              >
                {eyebrowIcon}
              </span>
            ) : null}
            {eyebrow}
          </p>
          <h1
            id={titleId}
            className="mt-4 text-3xl font-bold leading-tight tracking-tight sm:text-[44px] sm:leading-[1.08]"
          >
            {title}
          </h1>
          {description ? (
            <p className="mt-3.5 max-w-[620px] text-base leading-relaxed text-neutral-300">
              {description}
            </p>
          ) : null}
          {children}
        </div>
        {aside ? <div className="min-w-0">{aside}</div> : null}
      </div>
      {tabs}
    </JudgeDarkFrame>
  );
}
